/**
 * Il carico della partita conclusa, la scheda Carico di Modifica (V18): si
 * registra o si modifica qui, dopo la conferma che apre la modifica.
 *
 * Sopra la scheda di tipo partita, sotto una riga per chi ha giocato, in
 * ordine di cognome, come nel Registro seduta: maglia, nome, minuti in
 * partita e i due campi minuti e RPE. Chi ha minuti registrati diversi da
 * quelli delle partecipazioni ha l'avviso sulla riga, con i due valori.
 * Sostituire un carico già scritto chiede conferma.
 */

import { type FormEvent, useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ClockIcon, GaugeIcon, OctagonXIcon, TriangleAlertIcon } from 'lucide-react';
import { toast } from 'sonner';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { PulsanteInvio } from '../comuni/Azioni.js';
import { chiama } from '../api/client.js';
import { useConferma } from '../comuni/Conferma.js';
import { CardVista } from '../comuni/Riquadri.js';
import { Nota } from '../comuni/Nota.js';
import { usePartita } from '../partite/contesto.js';
import { messaggio } from '../comuni/dati.js';
import { Maglia } from '../comuni/Maglia.js';
import { StatoCaricamento } from '../comuni/Testata.js';
import type { SchedaInElenco } from '../schede/schede.js';
import type { CaricoPartitaLetto } from './carichi.js';
import { StatoVuoto } from '../comuni/StatoVuoto.js';

type Riga = { rpe: string; minuti: string };

export function CaricoPartita() {
  const { id: partitaId } = useParams<{ id: string }>();
  const partita = usePartita();
  const [carico, setCarico] = useState<CaricoPartitaLetto | null>(null);
  const [schede, setSchede] = useState<SchedaInElenco[]>([]);
  const [schedaId, setSchedaId] = useState('');
  const [righe, setRighe] = useState<Record<string, Riga>>({});
  const [erroreLettura, setErroreLettura] = useState<string | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);
  const [chiedi, conferma] = useConferma();

  const leggi = useCallback(() => {
    setErroreLettura(null);
    void Promise.all([
      chiama<CaricoPartitaLetto>(`/partite/${partitaId}/carico`),
      chiama<SchedaInElenco[]>('/schede?tipo=partita'),
    ])
      .then(([letto, elenco]) => {
        const attive = elenco.filter((s) => s.attiva && !s.schedaBaseId);
        setCarico(letto);
        setSchede(attive);
        setSchedaId(letto.scheda?.id ?? attive[0]?.id ?? '');
        const perLog = new Map(letto.log.map((l) => [l.giocatoreId, l]));
        setRighe(
          Object.fromEntries(
            letto.partecipanti.map((p) => {
              const log = perLog.get(p.giocatoreId);
              return [
                p.giocatoreId,
                {
                  rpe: log ? String(log.rpeEseguito) : '',
                  minuti: String(log?.minutiRegistrati ?? p.minutiPartecipazione),
                },
              ];
            }),
          ),
        );
      })
      .catch((e) => setErroreLettura(messaggio(e)));
  }, [partitaId]);

  useEffect(() => leggi(), [leggi]);

  const cambia = (giocatoreId: string, campo: keyof Riga, valore: string) =>
    setRighe((prev) => ({ ...prev, [giocatoreId]: { ...prev[giocatoreId]!, [campo]: valore } }));

  const salva = async (e: FormEvent) => {
    e.preventDefault();
    if (!schedaId || !carico) return;
    if (
      carico.esiste &&
      !(await chiedi({
        titolo: 'Sostituire il carico registrato?',
        testo:
          "Il blocco e i log di questa partita vengono sostituiti. L'azione resta nel registro.",
        azione: 'Sostituisci',
      }))
    ) {
      return;
    }
    setInCorso(true);
    setErrore(null);
    try {
      await chiama(`/partite/${partitaId}/carico`, {
        metodo: 'POST',
        corpo: {
          schedaId,
          sostituisci: carico.esiste || undefined,
          log: carico.partecipanti
            .map((p) => ({ giocatoreId: p.giocatoreId, ...righe[p.giocatoreId]! }))
            .filter((r) => r.rpe !== '')
            .map((r) => ({
              giocatoreId: r.giocatoreId,
              rpeEseguito: Number(r.rpe),
              minutiEseguiti: Number(r.minuti),
            })),
        },
      });
      toast.success(carico.esiste ? 'Carico sostituito.' : 'Carico registrato.');
      // Si rilegge il carico salvato e l'avviso dei minuti in testata.
      leggi();
      await partita?.ricarica();
    } catch (e) {
      setErrore(messaggio(e));
    } finally {
      setInCorso(false);
    }
  };

  const perLog = new Map((carico?.log ?? []).map((l) => [l.giocatoreId, l]));
  const diversi = carico?.log.filter((l) => l.minutiDiversi).length ?? 0;

  if (!carico) return <StatoCaricamento errore={erroreLettura} />;

  return (
    <>
      <CardVista
        titolo={carico.esiste ? 'Carico registrato' : 'Carico da registrare'}
        Icona={GaugeIcon}
        conteggio={carico.partecipanti.length}
      >
        <form className="flex flex-col gap-4" onSubmit={(e) => void salva(e)}>
          <Nota>Scheda di tipo partita, minuti dalle partecipazioni, RPE obbligatorio.</Nota>
          {errore && (
            <Alert variant="destructive">
              <OctagonXIcon />
              <AlertTitle>Salvataggio non riuscito</AlertTitle>
              <AlertDescription>{errore}</AlertDescription>
            </Alert>
          )}
          {diversi > 0 && (
            <Alert>
              <TriangleAlertIcon />
              <AlertTitle>Minuti cambiati</AlertTitle>
              <AlertDescription>
                Per {diversi === 1 ? 'un giocatore' : `${diversi} giocatori`} i minuti registrati
                non coincidono più con le partecipazioni. Salvando si aggiornano.
              </AlertDescription>
            </Alert>
          )}
          <FieldGroup className="max-w-2xl">
            <Field>
              <FieldLabel htmlFor="carico-scheda">Scheda</FieldLabel>
              <Select value={schedaId} onValueChange={setSchedaId}>
                <SelectTrigger id="carico-scheda" className="w-full bg-card">
                  <SelectValue placeholder="Scegli una scheda" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {schede.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.nome}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </Field>
          </FieldGroup>
          {carico.partecipanti.length === 0 ? (
            <StatoVuoto titolo="Nessun giocatore con minuti di partecipazione" />
          ) : (
            <ul className="grid gap-2 @5xl:grid-cols-2">
              {carico.partecipanti.map((p) => {
                const riga = righe[p.giocatoreId];
                const log = perLog.get(p.giocatoreId);
                const nome = `${p.cognome} ${p.nome}`;
                return (
                  <li
                    key={p.giocatoreId}
                    className="flex items-center gap-3 rounded-lg border border-border bg-card p-2"
                  >
                    <Maglia numero={p.numero} ruolo={p.ruoloPreferito} />
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span className="truncate font-semibold">{nome}</span>
                      <span
                        className={cn(
                          'flex items-center gap-1 text-[13px] tabular-nums',
                          log?.minutiDiversi
                            ? 'font-medium text-stato-recupero'
                            : 'text-muted-foreground',
                        )}
                      >
                        {log?.minutiDiversi ? (
                          <TriangleAlertIcon aria-hidden className="size-3.5" />
                        ) : (
                          <ClockIcon aria-hidden className="size-3.5" />
                        )}
                        {p.minutiPartecipazione}′ in partita
                        {log?.minutiDiversi && `, registrati ${log.minutiRegistrati}′`}
                      </span>
                    </span>
                    <span className="flex items-center gap-1">
                      <ClockIcon aria-hidden className="size-4 text-muted-foreground" />
                      <Input
                        aria-label={`${nome} · minuti`}
                        className="h-8 w-14 px-1 text-center tabular-nums"
                        inputMode="numeric"
                        min={0}
                        type="number"
                        value={riga?.minuti ?? ''}
                        onChange={(e) => cambia(p.giocatoreId, 'minuti', e.target.value)}
                      />
                    </span>
                    <span className="flex items-center gap-1">
                      <GaugeIcon aria-hidden className="size-4 text-muted-foreground" />
                      <Input
                        required
                        aria-label={`${nome} · RPE`}
                        placeholder="RPE"
                        className="h-8 w-12 px-1 text-center tabular-nums"
                        inputMode="numeric"
                        min={1}
                        max={10}
                        type="number"
                        value={riga?.rpe ?? ''}
                        onChange={(e) => cambia(p.giocatoreId, 'rpe', e.target.value)}
                      />
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
          <div className="flex justify-end">
            <PulsanteInvio type="submit" inCorso={inCorso} disabled={!schedaId}>
              {carico.esiste ? 'Sostituisci il carico' : 'Registra il carico'}
            </PulsanteInvio>
          </div>
        </form>
      </CardVista>
      {conferma}
    </>
  );
}
